import {
  estimateMonthlyCost,
  fetchApps,
  fetchDiskAttachments,
  fetchPersistentDisks,
  fetchServices,
  selectAppsAsList,
  selectDiskAttachmentsAsList,
  selectPersistentDisksByEnvId,
  selectServicesAsList,
} from "@app/deploy";
import { useQuery, useSelector } from "@app/react";
import { appServiceDisksPathUrl } from "@app/routes";
import { schema } from "@app/schema";
import { capitalize } from "@app/string-utils";
import type { DeployDiskAttachment, DeployPersistentDisk } from "@app/types";
import { ButtonCreate } from "@app/ui/shared/button";
import { costColDef } from "@app/ui/shared/cost-estimate-tooltip";
import {
  CurrencyCell,
  DataTable,
  DataTableCell,
  type DataTableCellRendererProps,
  type DataTableColDef,
  DateTimeCell,
  idColDef,
} from "@app/ui/shared/datatable";
import { DropdownMenu } from "@app/ui/shared/dropdown-menu";
import { Group } from "@app/ui/shared/group";
import {
  IconCylinder,
  IconEllipsisHorizontal,
  IconPlusCircle,
} from "@app/ui/shared/icons";
import { tokens } from "@app/ui/shared/tokens";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AttachPersistentDiskModal } from "./attach-persistent-disk-modal";
import { CreatePersistentDiskModal } from "./create-persistent-disk-modal";
import { DeletePersistentDiskModal } from "./delete-persistent-disk-modal";
import { DetachPersistentDiskModal } from "./detach-persistent-disk-modal";
import { ResizePersistentDiskModal } from "./resize-persistent-disk-modal";

interface ActiveAttachment {
  attachment: DeployDiskAttachment;
  // Raw service handle, used by the Detach modal copy. Not shown directly in
  // the table.
  serviceHandle: string;
  // Display label for the service in the "Attached To" cell. Prefer the
  // service's process type ("CMD", "web", ...) over its raw handle: the raw
  // handle includes an internal random suffix (e.g. "...-cmd-auylav") that
  // we don't expose to customers elsewhere in the UI.
  serviceLabel: string;
  serviceId: string;
  appId: string;
  appHandle: string;
}

interface PersistentDiskRow extends DeployPersistentDisk {
  // Active (non-deprovisioned) attachments, with the service handle resolved.
  activeAttachments: ActiveAttachment[];
}

// State for the detach modal: which attachment (on which disk) to detach.
interface DetachTarget {
  disk: DeployPersistentDisk;
  attachment: DeployDiskAttachment;
  serviceHandle: string;
}

// Renders the cost for a single disk row. Pulled into its own component so the
// cost-rates selector can be read via a hook from inside the cell renderer.
const DiskCostCell = ({ disk }: { disk: PersistentDiskRow }) => {
  const rates = useSelector(schema.costRates.select);
  const cost = estimateMonthlyCost({
    rates,
    persistentDisks: [{ size: disk.size, provisionedIops: 0 }],
  });
  return <CurrencyCell value={cost} />;
};

// Per-row "..." overflow menu. Guards: Resize requires a provisioned disk;
// Attach requires a provisioned, unattached disk (a disk has at most one
// active attachment; paths change via detach -> re-attach); Detach requires an
// active attachment; Delete is blocked while attachments remain.
const DiskActionsMenu = ({
  disk,
  onAttach,
  onDetach,
  onResize,
  onDelete,
}: {
  disk: PersistentDiskRow;
  onAttach: (disk: DeployPersistentDisk) => void;
  onDetach: (target: DetachTarget) => void;
  onResize: (disk: DeployPersistentDisk) => void;
  onDelete: (disk: DeployPersistentDisk) => void;
}) => {
  // A disk must be provisioned before it can be attached, resized, etc.
  const isProvisioned = disk.status === "provisioned";
  const hasAttachments = disk.activeAttachments.length > 0;
  // Detach acts on a specific attachment; the menu targets the first active one.
  const firstActive = disk.activeAttachments[0];

  return (
    <DropdownMenu
      label={<IconEllipsisHorizontal variant="sm" />}
      items={[
        {
          id: "attach",
          label: "Attach",
          // A disk has at most one active attachment; paths change via
          // detach -> re-attach. An already-attached disk cannot be attached
          // anywhere, so Attach requires a provisioned, unattached disk.
          disabled: !isProvisioned || hasAttachments,
          onClick: (close) => {
            onAttach(disk);
            close();
          },
        },
        {
          id: "detach",
          label: "Detach",
          disabled: !hasAttachments || firstActive == null,
          onClick: (close) => {
            if (firstActive) {
              onDetach({
                disk,
                attachment: firstActive.attachment,
                serviceHandle: firstActive.serviceHandle,
              });
            }
            close();
          },
        },
        {
          id: "resize",
          label: "Resize",
          disabled: !isProvisioned,
          onClick: (close) => {
            onResize(disk);
            close();
          },
        },
        {
          id: "delete",
          label: "Delete",
          hasSeparator: true,
          className: "text-red-500",
          // Block delete while the disk still has active attachments.
          disabled: hasAttachments,
          onClick: (close) => {
            onDelete(disk);
            close();
          },
        },
      ]}
    />
  );
};

export const PersistentDiskListByEnvironment = ({
  envId,
}: {
  envId: string;
}) => {
  // Backed by the org-wide /disk_attachments endpoint.
  useQuery(fetchPersistentDisks());
  useQuery(fetchDiskAttachments());
  useQuery(fetchServices());
  useQuery(fetchApps());

  const [isOpen, setIsOpen] = useState(false);
  const [attachDisk, setAttachDisk] = useState<DeployPersistentDisk | null>(
    null,
  );
  const [detachTarget, setDetachTarget] = useState<DetachTarget | null>(null);
  const [resizeDisk, setResizeDisk] = useState<DeployPersistentDisk | null>(
    null,
  );
  const [deleteDisk, setDeleteDisk] = useState<DeployPersistentDisk | null>(
    null,
  );
  const disks = useSelector((s) => selectPersistentDisksByEnvId(s, { envId }));
  const attachments = useSelector(selectDiskAttachmentsAsList);
  const services = useSelector(selectServicesAsList);
  const apps = useSelector(selectAppsAsList);

  const rows = useMemo<PersistentDiskRow[]>(() => {
    const appHandleById = new Map(apps.map((a) => [a.id, a.handle]));
    const serviceById = new Map(services.map((s) => [s.id, s]));
    return disks.map((d) => {
      const activeAttachments: ActiveAttachment[] = attachments
        .filter(
          (a) => a.persistentDiskId === d.id && a.status !== "deprovisioned",
        )
        .map((a) => {
          const svc = serviceById.get(a.serviceId);
          return {
            attachment: a,
            serviceHandle: svc?.handle || "",
            // processType ("CMD", "web", ...) is the customer-facing service
            // label; the raw handle is used as a fallback when the service
            // record isn't hydrated yet.
            serviceLabel: svc?.processType || svc?.handle || "",
            serviceId: svc?.id ?? "",
            appId: svc?.appId ?? "",
            appHandle: svc ? (appHandleById.get(svc.appId) ?? "") : "",
          };
        });
      return { ...d, activeAttachments };
    });
  }, [disks, attachments, services, apps]);

  // Column order: Handle, ID, Attached To, Mount Path, Disk Size, Status,
  // Created, Cost/mo, Actions.
  const colDefs = useMemo<DataTableColDef<PersistentDiskRow>[]>(
    () => [
      {
        field: "handle",
        headerName: "Handle",
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<PersistentDiskRow>) => (
          <Group variant="horizontal" size="sm" className="items-center">
            <IconCylinder variant="sm" className="text-gray-500 shrink-0" />
            <DataTableCell label={data.handle} />
          </Group>
        ),
      },
      {
        field: "id",
        ...idColDef,
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<PersistentDiskRow>) => (
          <DataTableCell label={data.id} />
        ),
      },
      {
        // Renderer-only column (no matching key on the row) -> use colId.
        colId: "attachedTo",
        headerName: "Attached To",
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<PersistentDiskRow>) => {
          if (data.activeAttachments.length === 0) {
            return <span className="text-gray-400 italic">None</span>;
          }
          // First line: the service's process type ("CMD", "web", ...), never
          // the raw handle, which carries an internal random suffix. Linked to
          // the service's Disk Storage tab so the customer can drill in.
          // Second line: parent app handle in muted color. Multi-attachment is
          // rare today but renders all rows on separate lines so nothing is
          // hidden.
          return (
            <div>
              {data.activeAttachments.map((a, i) => (
                <div key={a.attachment.id} className={i > 0 ? "mt-1" : ""}>
                  {a.appId && a.serviceId ? (
                    <Link
                      to={appServiceDisksPathUrl(a.appId, a.serviceId)}
                      className={`${tokens.type.darker} hover:underline`}
                    >
                      {a.serviceLabel}
                    </Link>
                  ) : (
                    <span className={tokens.type.darker}>{a.serviceLabel}</span>
                  )}
                  {a.appHandle ? (
                    <div className={tokens.type["normal lighter"]}>
                      {a.appHandle}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        // Renderer-only column (no matching key on the row) -> use colId.
        colId: "mountPath",
        headerName: "Mount Path",
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<PersistentDiskRow>) => {
          const mountPoints = data.activeAttachments
            .map((a) => a.attachment.mountPoint)
            .filter((m) => Boolean(m));
          return mountPoints.length === 0 ? (
            <span className="text-gray-400">-</span>
          ) : (
            <DataTableCell label={mountPoints.join(", ")} />
          );
        },
      },
      {
        field: "size",
        headerName: "Disk Size",
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<PersistentDiskRow>) => (
          <DataTableCell label={`${data.size} GB`} />
        ),
      },
      {
        field: "status",
        headerName: "Status",
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<PersistentDiskRow>) => (
          <DataTableCell label={capitalize(data.status)} />
        ),
      },
      {
        field: "createdAt",
        headerName: "Created",
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<PersistentDiskRow>) => (
          <DateTimeCell date={data.createdAt} />
        ),
      },
      {
        // Computed column (no matching key on the row) -> use colId.
        ...costColDef,
        colId: "cost",
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<PersistentDiskRow>) => (
          <DiskCostCell disk={data} />
        ),
      },
      {
        colId: "actions",
        headerName: "Actions",
        sortable: false,
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<PersistentDiskRow>) => (
          <DiskActionsMenu
            disk={data}
            onAttach={setAttachDisk}
            onDetach={setDetachTarget}
            onResize={setResizeDisk}
            onDelete={setDeleteDisk}
          />
        ),
      },
    ],
    [],
  );

  return (
    <Group>
      <div className="flex justify-end">
        <ButtonCreate envId={envId} onClick={() => setIsOpen(true)}>
          <IconPlusCircle variant="sm" />
          <div className="ml-2">New Disk</div>
        </ButtonCreate>
      </div>

      <DataTable<PersistentDiskRow> rowData={rows} colDefs={colDefs} />

      <CreatePersistentDiskModal
        isOpen={isOpen}
        envId={envId}
        onClose={() => setIsOpen(false)}
      />

      {attachDisk ? (
        <AttachPersistentDiskModal
          isOpen={attachDisk !== null}
          disk={attachDisk}
          onClose={() => setAttachDisk(null)}
        />
      ) : null}

      {detachTarget ? (
        <DetachPersistentDiskModal
          isOpen={detachTarget !== null}
          disk={detachTarget.disk}
          attachment={detachTarget.attachment}
          serviceHandle={detachTarget.serviceHandle}
          onClose={() => setDetachTarget(null)}
        />
      ) : null}

      {resizeDisk ? (
        <ResizePersistentDiskModal
          isOpen={resizeDisk !== null}
          disk={resizeDisk}
          onClose={() => setResizeDisk(null)}
        />
      ) : null}

      {deleteDisk ? (
        <DeletePersistentDiskModal
          isOpen={deleteDisk !== null}
          disk={deleteDisk}
          onClose={() => setDeleteDisk(null)}
        />
      ) : null}
    </Group>
  );
};
